// Offline-First Supabase Proxy
// Intercepts all Supabase calls and redirects to IndexedDB for offline-first operation

import { ALTZERO_TABLES, AltzeroTable, getOfflineDB } from "./offlineDB";
import { supabase as originalSupabase } from "./supabaseClient";

// Query builder interface for offline operations
export interface OfflineQueryBuilder {
  select(columns?: string): OfflineQueryBuilder;
  insert(data: any): OfflineQueryBuilder;
  update(data: any): OfflineQueryBuilder;
  delete(): OfflineQueryBuilder;
  upsert(data: any): OfflineQueryBuilder;
  eq(column: string, value: any): OfflineQueryBuilder;
  neq(column: string, value: any): OfflineQueryBuilder;
  in(column: string, values: any[]): OfflineQueryBuilder;
  order(column: string, options?: { ascending?: boolean }): OfflineQueryBuilder;
  limit(count: number): OfflineQueryBuilder;
  single(): OfflineQueryBuilder;
  then(onFulfilled?: (value: any) => any, onRejected?: (reason: any) => any): Promise<any>;
}

// Offline query result
export interface OfflineQueryResult {
  data: any[] | any | null;
  error: any | null;
  count?: number;
}

// Mutation queue for offline changes
interface QueuedMutation {
  id: string;
  tableName: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE' | 'UPSERT';
  data: any;
  filters?: any;
  timestamp: number;
}

class OfflineQueryBuilderImpl implements OfflineQueryBuilder {
  private tableName: string;
  private userId: string;
  private selectColumns: string = '*';
  private filters: Array<{ column: string; operator: string; value: any }> = [];
  private orderBy: { column: string; ascending: boolean } | null = null;
  private limitCount: number | null = null;
  private singleResult: boolean = false;
  private operation: 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE' | 'UPSERT' = 'SELECT';
  private operationData: any = null;

  constructor(tableName: string, userId: string) {
    this.tableName = tableName;
    this.userId = userId;
  }

  select(columns: string = '*'): OfflineQueryBuilder {
    this.operation = 'SELECT';
    this.selectColumns = columns;
    return this;
  }

  insert(data: any): OfflineQueryBuilder {
    this.operation = 'INSERT';
    this.operationData = data;
    return this;
  }

  update(data: any): OfflineQueryBuilder {
    this.operation = 'UPDATE';
    this.operationData = data;
    return this;
  }

  delete(): OfflineQueryBuilder {
    this.operation = 'DELETE';
    return this;
  }

  upsert(data: any): OfflineQueryBuilder {
    this.operation = 'UPSERT';
    this.operationData = data;
    return this;
  }

  eq(column: string, value: any): OfflineQueryBuilder {
    this.filters.push({ column, operator: 'eq', value });
    return this;
  }

  neq(column: string, value: any): OfflineQueryBuilder {
    this.filters.push({ column, operator: 'neq', value });
    return this;
  }

  in(column: string, values: any[]): OfflineQueryBuilder {
    this.filters.push({ column, operator: 'in', value: values });
    return this;
  }

  order(column: string, options: { ascending?: boolean } = {}): OfflineQueryBuilder {
    this.orderBy = { column, ascending: options.ascending !== false };
    return this;
  }

  limit(count: number): OfflineQueryBuilder {
    this.limitCount = count;
    return this;
  }

  single(): OfflineQueryBuilder {
    this.singleResult = true;
    return this;
  }

  async then(onFulfilled?: (value: any) => any, onRejected?: (reason: any) => any): Promise<any> {
    try {
      const result = await this.execute();
      return onFulfilled ? onFulfilled(result) : result;
    } catch (error) {
      return onRejected ? onRejected(error) : Promise.reject(error);
    }
  }

  private async execute(): Promise<OfflineQueryResult> {
    try {
      const offlineDB = getOfflineDB(this.userId);

      switch (this.operation) {
        case 'SELECT':
          return await this.executeSelect(offlineDB);
        case 'INSERT':
          return await this.executeInsert(offlineDB);
        case 'UPDATE':
          return await this.executeUpdate(offlineDB);
        case 'DELETE':
          return await this.executeDelete(offlineDB);
        case 'UPSERT':
          return await this.executeUpsert(offlineDB);
        default:
          throw new Error(`Unsupported operation: ${this.operation}`);
      }
    } catch (error) {
      console.error('[OfflineProxy] Query execution failed:', error);
      return {
        data: null,
        error: error
      };
    }
  }

  private async executeSelect(offlineDB: any): Promise<OfflineQueryResult> {
    console.log(`[OfflineProxy] Executing SELECT on ${this.tableName} for user ${this.userId}`);
    console.log(`[OfflineProxy] Filters:`, this.filters);

    let data = await offlineDB.getAll(this.tableName as AltzeroTable);
    console.log(`[OfflineProxy] Retrieved ${data.length} records from offline DB`);

    // Apply filters
    data = this.applyFilters(data);
    console.log(`[OfflineProxy] After filters: ${data.length} records`);

    // Apply ordering
    if (this.orderBy) {
      data = this.applyOrdering(data);
      console.log(`[OfflineProxy] Applied ordering by ${this.orderBy.column}`);
    }

    // Apply limit
    if (this.limitCount) {
      data = data.slice(0, this.limitCount);
      console.log(`[OfflineProxy] Applied limit: ${data.length} records`);
    }

    // Return single result if requested
    if (this.singleResult) {
      const result = {
        data: data.length > 0 ? data[0] : null,
        error: null
      };
      console.log(`[OfflineProxy] Returning single result:`, result.data ? 'Found' : 'Not found');
      return result;
    }

    const result = {
      data,
      error: null,
      count: data.length
    };
    console.log(`[OfflineProxy] Returning ${result.count} records`);
    return result;
  }

  private async executeInsert(offlineDB: any): Promise<OfflineQueryResult> {
    const isOnline = navigator.onLine;
    const tableName = this.tableName as AltzeroTable;
    
    // Check if this is a cached table
    if (!ALTZERO_TABLES.includes(tableName)) {
      // Not a cached table, fall back to Supabase
      console.log('[OfflineProxy] INSERT operation - table not cached, using Supabase');
      const result = await originalSupabase.schema('altzero').from(this.tableName).insert(this.operationData);
      return result;
    }

    // Prepare data with timestamp
    const dataToInsert = Array.isArray(this.operationData) 
      ? this.operationData.map(item => ({ ...item, updated_at: new Date().toISOString() }))
      : { ...this.operationData, updated_at: new Date().toISOString() };

    if (isOnline) {
      try {
        // Execute on Supabase first
        const result = await originalSupabase.schema('altzero').from(this.tableName).insert(this.operationData);
        
        if (result.error) {
          return { data: null, error: result.error };
        }

        // Update IndexedDB after successful Supabase operation
        await this.updateIndexedDBAfterMutation(tableName, 'INSERT', result.data || dataToInsert);
        
        return { data: result.data, error: null };
      } catch (error) {
        console.error('[OfflineProxy] INSERT failed on Supabase:', error);
        return { data: null, error };
      }
    } else {
      // Offline: Update IndexedDB optimistically and queue mutation
      console.log('[OfflineProxy] INSERT operation - offline, updating IndexedDB and queuing');
      
      try {
        // Update IndexedDB optimistically
        await this.updateIndexedDBAfterMutation(tableName, 'INSERT', dataToInsert);
        
        // Queue mutation for sync
        const proxy = OfflineSupabaseProxy.getInstance();
        proxy.queueMutation({
          tableName: this.tableName,
          operation: 'INSERT',
          data: this.operationData,
          filters: this.getFiltersObject()
        });
        
        return { data: Array.isArray(dataToInsert) ? dataToInsert : [dataToInsert], error: null };
      } catch (error) {
        console.error('[OfflineProxy] Failed to update IndexedDB:', error);
        return { data: null, error };
      }
    }
  }

  private async executeUpdate(offlineDB: any): Promise<OfflineQueryResult> {
    const isOnline = navigator.onLine;
    const tableName = this.tableName as AltzeroTable;
    
    // Check if this is a cached table
    if (!ALTZERO_TABLES.includes(tableName)) {
      // Not a cached table, fall back to Supabase
      console.log('[OfflineProxy] UPDATE operation - table not cached, using Supabase');
      let query = originalSupabase.schema('altzero').from(this.tableName).update(this.operationData);
      this.filters.forEach(filter => {
        if (filter.operator === 'eq') {
          query = query.eq(filter.column, filter.value);
        }
      });
      return await query;
    }

    // Prepare update data with timestamp
    const updateData = { ...this.operationData, updated_at: new Date().toISOString() };
    const filtersObj = this.getFiltersObject();

    if (isOnline) {
      try {
        // Execute on Supabase first
        let query = originalSupabase.schema('altzero').from(this.tableName).update(this.operationData);
        this.filters.forEach(filter => {
          if (filter.operator === 'eq') {
            query = query.eq(filter.column, filter.value);
          }
        });
        const result = await query;
        
        if (result.error) {
          return { data: null, error: result.error };
        }

        // Update IndexedDB after successful Supabase operation
        await this.updateIndexedDBAfterMutation(tableName, 'UPDATE', updateData, filtersObj);
        
        return { data: result.data, error: null };
      } catch (error) {
        console.error('[OfflineProxy] UPDATE failed on Supabase:', error);
        return { data: null, error };
      }
    } else {
      // Offline: Update IndexedDB optimistically and queue mutation
      console.log('[OfflineProxy] UPDATE operation - offline, updating IndexedDB and queuing');
      
      try {
        // Update IndexedDB optimistically
        await this.updateIndexedDBAfterMutation(tableName, 'UPDATE', updateData, filtersObj);
        
        // Queue mutation for sync
        const proxy = OfflineSupabaseProxy.getInstance();
        proxy.queueMutation({
          tableName: this.tableName,
          operation: 'UPDATE',
          data: this.operationData,
          filters: filtersObj
        });
        
        return { data: [updateData], error: null };
      } catch (error) {
        console.error('[OfflineProxy] Failed to update IndexedDB:', error);
        return { data: null, error };
      }
    }
  }

  private async executeDelete(offlineDB: any): Promise<OfflineQueryResult> {
    const isOnline = navigator.onLine;
    const tableName = this.tableName as AltzeroTable;
    
    // Check if this is a cached table
    if (!ALTZERO_TABLES.includes(tableName)) {
      // Not a cached table, fall back to Supabase
      console.log('[OfflineProxy] DELETE operation - table not cached, using Supabase');
      let query = originalSupabase.schema('altzero').from(this.tableName).delete();
      this.filters.forEach(filter => {
        if (filter.operator === 'eq') {
          query = query.eq(filter.column, filter.value);
        }
      });
      return await query;
    }

    const filtersObj = this.getFiltersObject();

    if (isOnline) {
      try {
        // Execute on Supabase first
        let query = originalSupabase.schema('altzero').from(this.tableName).delete();
        this.filters.forEach(filter => {
          if (filter.operator === 'eq') {
            query = query.eq(filter.column, filter.value);
          }
        });
        const result = await query;
        
        if (result.error) {
          return { data: null, error: result.error };
        }

        // Remove from IndexedDB after successful Supabase operation
        await this.updateIndexedDBAfterMutation(tableName, 'DELETE', null, filtersObj);
        
        return { data: result.data, error: null };
      } catch (error) {
        console.error('[OfflineProxy] DELETE failed on Supabase:', error);
        return { data: null, error };
      }
    } else {
      // Offline: Remove from IndexedDB immediately and queue mutation
      console.log('[OfflineProxy] DELETE operation - offline, removing from IndexedDB and queuing');
      
      try {
        // Remove from IndexedDB immediately
        await this.updateIndexedDBAfterMutation(tableName, 'DELETE', null, filtersObj);
        
        // Queue mutation for sync
        const proxy = OfflineSupabaseProxy.getInstance();
        proxy.queueMutation({
          tableName: this.tableName,
          operation: 'DELETE',
          data: null,
          filters: filtersObj
        });
        
        return { data: [], error: null };
      } catch (error) {
        console.error('[OfflineProxy] Failed to update IndexedDB:', error);
        return { data: null, error };
      }
    }
  }

  private async executeUpsert(offlineDB: any): Promise<OfflineQueryResult> {
    const isOnline = navigator.onLine;
    const tableName = this.tableName as AltzeroTable;
    
    // Check if this is a cached table
    if (!ALTZERO_TABLES.includes(tableName)) {
      // Not a cached table, fall back to Supabase
      console.log('[OfflineProxy] UPSERT operation - table not cached, using Supabase');
      const result = await originalSupabase.schema('altzero').from(this.tableName).upsert(this.operationData);
      return result;
    }

    // Prepare data with timestamp
    const dataToUpsert = Array.isArray(this.operationData) 
      ? this.operationData.map(item => ({ ...item, updated_at: new Date().toISOString() }))
      : { ...this.operationData, updated_at: new Date().toISOString() };

    if (isOnline) {
      try {
        // Execute on Supabase first
        const result = await originalSupabase.schema('altzero').from(this.tableName).upsert(this.operationData);
        
        if (result.error) {
          return { data: null, error: result.error };
        }

        // Update IndexedDB after successful Supabase operation
        await this.updateIndexedDBAfterMutation(tableName, 'UPSERT', result.data || dataToUpsert);
        
        return { data: result.data, error: null };
      } catch (error) {
        console.error('[OfflineProxy] UPSERT failed on Supabase:', error);
        return { data: null, error };
      }
    } else {
      // Offline: Update IndexedDB optimistically and queue mutation
      console.log('[OfflineProxy] UPSERT operation - offline, updating IndexedDB and queuing');
      
      try {
        // Update IndexedDB optimistically
        await this.updateIndexedDBAfterMutation(tableName, 'UPSERT', dataToUpsert);
        
        // Queue mutation for sync
        const proxy = OfflineSupabaseProxy.getInstance();
        proxy.queueMutation({
          tableName: this.tableName,
          operation: 'UPSERT',
          data: this.operationData,
          filters: this.getFiltersObject()
        });
        
        return { data: Array.isArray(dataToUpsert) ? dataToUpsert : [dataToUpsert], error: null };
      } catch (error) {
        console.error('[OfflineProxy] Failed to update IndexedDB:', error);
        return { data: null, error };
      }
    }
  }

  private applyFilters(data: any[]): any[] {
    return data.filter(record => {
      return this.filters.every(filter => {
        const recordValue = record[filter.column];
        
        switch (filter.operator) {
          case 'eq':
            return recordValue === filter.value;
          case 'neq':
            return recordValue !== filter.value;
          case 'in':
            return filter.value.includes(recordValue);
          default:
            return true;
        }
      });
    });
  }

  private applyOrdering(data: any[]): any[] {
    if (!this.orderBy) return data;

    return [...data].sort((a, b) => {
      const aVal = a[this.orderBy!.column];
      const bVal = b[this.orderBy!.column];

      let comparison = 0;
      if (aVal < bVal) comparison = -1;
      else if (aVal > bVal) comparison = 1;

      return this.orderBy!.ascending ? comparison : -comparison;
    });
  }

  /**
   * Convert filters array to object for mutation queue
   */
  private getFiltersObject(): Record<string, any> {
    const filtersObj: Record<string, any> = {};
    this.filters.forEach(filter => {
      if (filter.operator === 'eq') {
        filtersObj[filter.column] = filter.value;
      }
    });
    return filtersObj;
  }

  /**
   * Update IndexedDB after a mutation operation
   */
  private async updateIndexedDBAfterMutation(
    tableName: AltzeroTable,
    operation: 'INSERT' | 'UPDATE' | 'DELETE' | 'UPSERT',
    data: any | any[] | null,
    filters?: Record<string, any>
  ): Promise<void> {
    try {
      const offlineDB = getOfflineDB(this.userId);
      await offlineDB.ensureReady();

      if (operation === 'DELETE') {
        // Delete from IndexedDB
        if (filters) {
          // Find records matching filters and delete them
          const allRecords = await offlineDB.getAll(tableName);
          const recordsToDelete = allRecords.filter(record => {
            return Object.entries(filters).every(([key, value]) => record[key] === value);
          });

          for (const record of recordsToDelete) {
            const primaryKey = record.id;
            await new Promise<void>((resolve, reject) => {
              const transaction = offlineDB.db!.transaction([tableName], 'readwrite');
              const store = transaction.objectStore(tableName);
              const request = store.delete(primaryKey);
              request.onsuccess = () => resolve();
              request.onerror = () => reject(request.error);
            });
          }
        }
      } else {
        // Insert, Update, or Upsert
        const dataArray = Array.isArray(data) ? data : [data];
        for (const record of dataArray) {
          if (record) {
            await new Promise<void>((resolve, reject) => {
              const transaction = offlineDB.db!.transaction([tableName], 'readwrite');
              const store = transaction.objectStore(tableName);
              const request = store.put(record);
              request.onsuccess = () => resolve();
              request.onerror = () => reject(request.error);
            });
          }
        }
      }
    } catch (error) {
      console.error(`[OfflineProxy] Failed to update IndexedDB for ${operation}:`, error);
      throw error;
    }
  }
}

// Offline schema proxy
class OfflineSchemaProxy {
  private userId: string;

  constructor(userId: string) {
    this.userId = userId;
  }

  from(tableName: string): OfflineQueryBuilder {
    console.log(`[OfflineSchemaProxy] from() called for table: ${tableName}`);
    console.log(`[OfflineSchemaProxy] User ID: ${this.userId}`);
    console.log(`[OfflineSchemaProxy] Is altzero table: ${ALTZERO_TABLES.includes(tableName as AltzeroTable)}`);

    // Check if this is an altzero table that should be handled offline
    if (ALTZERO_TABLES.includes(tableName as AltzeroTable)) {
      console.log(`[OfflineSchemaProxy] ✅ Using offline DB for ${tableName}`);
      return new OfflineQueryBuilderImpl(tableName, this.userId);
    }

    // For non-altzero tables, fall back to original Supabase
    console.log(`[OfflineSchemaProxy] ⚠️ Table ${tableName} not in offline cache, using Supabase`);
    return originalSupabase.schema('altzero').from(tableName) as any;
  }
}

// Main offline Supabase proxy
export class OfflineSupabaseProxy {
  private static instance: OfflineSupabaseProxy;
  private userId: string | null = null;
  private mutationQueue: QueuedMutation[] = [];
  private isOnline: boolean = navigator.onLine;

  private constructor() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processMutationQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  static getInstance(): OfflineSupabaseProxy {
    if (!OfflineSupabaseProxy.instance) {
      OfflineSupabaseProxy.instance = new OfflineSupabaseProxy();
    }
    return OfflineSupabaseProxy.instance;
  }

  setUserId(userId: string): void {
    this.userId = userId;
    console.log(`[OfflineProxy] Set user ID: ${userId}`);
  }

  schema(schemaName: string): OfflineSchemaProxy | any {
    if (schemaName === 'altzero' && this.userId) {
      console.log('[OfflineProxy] Intercepting altzero schema access');
      return new OfflineSchemaProxy(this.userId);
    }

    // For other schemas, use original Supabase
    return originalSupabase.schema(schemaName);
  }

  // Proxy other Supabase methods to original client
  get auth() {
    return originalSupabase.auth;
  }

  get storage() {
    return originalSupabase.storage;
  }

  get functions() {
    return originalSupabase.functions;
  }

  get realtime() {
    return originalSupabase.realtime;
  }

  rpc(functionName: string, params?: any) {
    console.log(`[OfflineSupabaseProxy] 📞 RPC call: ${functionName}`, params);
    console.log(`[OfflineSupabaseProxy] User ID: ${this.userId}`);
    console.log(`[OfflineSupabaseProxy] Falling back to Supabase for RPC calls`);

    // Handle functions that need user_id parameter when called without auth context
    const functionsNeedingUserId = [
      'altzero.get_user_organizations',
      'altzero.get_user_teams',
      'altzero.get_user_permissions',
      'altzero.get_user_role',
      'altzero.get_user_profile',
      'altzero.get_user_pending_org_invitations',
      'altzero.get_user_pending_team_invitations',
      'altzero.is_org_admin',
      'altzero.is_group_admin'
    ];

    // If this function needs a user ID and we have one, add it to params
    if (functionsNeedingUserId.includes(functionName) && this.userId) {
      const enhancedParams = params || {};

      // Add user_id parameter if not already provided
      if (!enhancedParams.user_id && !enhancedParams.p_user_id && !enhancedParams.user_id_param) {
        // Determine the correct parameter name based on function
        if (functionName.includes('get_user_permissions')) {
          enhancedParams.user_id_param = this.userId;
        } else if (functionName.includes('is_org_admin')) {
          enhancedParams.p_user_id = this.userId;
        } else if (functionName.includes('is_group_admin')) {
          enhancedParams.p_user_id = this.userId;
        } else {
          enhancedParams.p_user_id = this.userId;
        }

        console.log(`[OfflineSupabaseProxy] Enhanced params with user ID:`, enhancedParams);
        return originalSupabase.rpc(functionName, enhancedParams);
      }
    }

    // For other functions or when user ID is already provided, pass through as-is
    return originalSupabase.rpc(functionName, params);
  }

  from(tableName: string) {
    console.log(`[OfflineSupabaseProxy] Direct from() called for table: ${tableName}`);
    console.log(`[OfflineSupabaseProxy] User ID: ${this.userId}`);
    console.log(`[OfflineSupabaseProxy] Is altzero table: ${ALTZERO_TABLES.includes(tableName as AltzeroTable)}`);

    // Direct table access - check if it's an altzero table
    if (this.userId && ALTZERO_TABLES.includes(tableName as AltzeroTable)) {
      console.log(`[OfflineSupabaseProxy] ✅ Using offline DB for ${tableName}`);
      return new OfflineQueryBuilderImpl(tableName, this.userId);
    }

    console.log(`[OfflineSupabaseProxy] ⚠️ Falling back to Supabase for ${tableName}`);
    return originalSupabase.from(tableName);
  }

  /**
   * Queue a mutation for later sync (public for use by query builder)
   */
  queueMutation(mutation: Omit<QueuedMutation, 'id' | 'timestamp'>): void {
    const queuedMutation: QueuedMutation = {
      ...mutation,
      id: crypto.randomUUID(),
      timestamp: Date.now()
    };

    this.mutationQueue.push(queuedMutation);
    console.log(`[OfflineProxy] Queued mutation:`, queuedMutation);

    // Try to process immediately if online
    if (this.isOnline) {
      this.processMutationQueue();
    }
  }

  /**
   * Process queued mutations when online
   */
  private async processMutationQueue(): Promise<void> {
    if (!this.isOnline || this.mutationQueue.length === 0) {
      return;
    }

    console.log(`[OfflineProxy] Processing ${this.mutationQueue.length} queued mutations`);

    const mutations = [...this.mutationQueue];
    this.mutationQueue = [];

    for (const mutation of mutations) {
      try {
        await this.executeMutation(mutation);
        console.log(`[OfflineProxy] Successfully synced mutation:`, mutation.id);
      } catch (error) {
        console.error(`[OfflineProxy] Failed to sync mutation:`, mutation.id, error);
        // Re-queue failed mutations
        this.mutationQueue.push(mutation);
      }
    }
  }

  /**
   * Execute a mutation against Supabase and update IndexedDB
   */
  private async executeMutation(mutation: QueuedMutation): Promise<void> {
    const { tableName, operation, data, filters } = mutation;
    const tableNameTyped = tableName as AltzeroTable;

    let result: any;

    switch (operation) {
      case 'INSERT':
        result = await originalSupabase.schema('altzero').from(tableName).insert(data);
        if (result.error) throw result.error;
        // Update IndexedDB after successful insert
        if (ALTZERO_TABLES.includes(tableNameTyped) && result.data) {
          await this.updateIndexedDBFromMutation(tableNameTyped, 'INSERT', result.data);
        }
        break;
      case 'UPDATE':
        let updateQuery = originalSupabase.schema('altzero').from(tableName).update(data);
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            updateQuery = updateQuery.eq(key, value);
          });
        }
        result = await updateQuery;
        if (result.error) throw result.error;
        // Update IndexedDB after successful update
        if (ALTZERO_TABLES.includes(tableNameTyped) && result.data) {
          await this.updateIndexedDBFromMutation(tableNameTyped, 'UPDATE', result.data);
        }
        break;
      case 'DELETE':
        let deleteQuery = originalSupabase.schema('altzero').from(tableName).delete();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            deleteQuery = deleteQuery.eq(key, value);
          });
        }
        result = await deleteQuery;
        if (result.error) throw result.error;
        // Update IndexedDB after successful delete
        if (ALTZERO_TABLES.includes(tableNameTyped)) {
          await this.updateIndexedDBFromMutation(tableNameTyped, 'DELETE', null, filters);
        }
        break;
      case 'UPSERT':
        result = await originalSupabase.schema('altzero').from(tableName).upsert(data);
        if (result.error) throw result.error;
        // Update IndexedDB after successful upsert
        if (ALTZERO_TABLES.includes(tableNameTyped) && result.data) {
          await this.updateIndexedDBFromMutation(tableNameTyped, 'UPSERT', result.data);
        }
        break;
    }
  }

  /**
   * Update IndexedDB from mutation result (used by mutation queue processor)
   */
  private async updateIndexedDBFromMutation(
    tableName: AltzeroTable,
    operation: 'INSERT' | 'UPDATE' | 'DELETE' | 'UPSERT',
    data: any | any[] | null,
    filters?: Record<string, any>
  ): Promise<void> {
    try {
      const offlineDB = getOfflineDB(this.userId!);
      await offlineDB.ensureReady();

      if (operation === 'DELETE') {
        if (filters) {
          const allRecords = await offlineDB.getAll(tableName);
          const recordsToDelete = allRecords.filter(record => {
            return Object.entries(filters).every(([key, value]) => record[key] === value);
          });

          for (const record of recordsToDelete) {
            const primaryKey = record.id;
            await new Promise<void>((resolve, reject) => {
              const transaction = offlineDB.db!.transaction([tableName], 'readwrite');
              const store = transaction.objectStore(tableName);
              const request = store.delete(primaryKey);
              request.onsuccess = () => resolve();
              request.onerror = () => reject(request.error);
            });
          }
        }
      } else if (data) {
        const dataArray = Array.isArray(data) ? data : [data];
        for (const record of dataArray) {
          if (record) {
            await new Promise<void>((resolve, reject) => {
              const transaction = offlineDB.db!.transaction([tableName], 'readwrite');
              const store = transaction.objectStore(tableName);
              const request = store.put(record);
              request.onsuccess = () => resolve();
              request.onerror = () => reject(request.error);
            });
          }
        }
      }
    } catch (error) {
      console.error(`[OfflineProxy] Failed to update IndexedDB from mutation:`, error);
      throw error;
    }
  }
}

// Create and export the proxy instance
export const offlineSupabase = OfflineSupabaseProxy.getInstance();
